import { ClientLoggingService } from '../../../shared/client-logging.service';
import {
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  ViewChild,
} from '@angular/core';

import { FormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatChipsModule } from '@angular/material/chips';
import { MatMenuModule } from '@angular/material/menu';
import { MatSelectModule } from '@angular/material/select';
import { TextFieldModule } from '@angular/cdk/text-field';
import {
  ATTACHMENT_LIMITS,
  ChatAttachment,
  ChatModelOption,
  DEFAULT_CHAT_MODEL_ID,
  DEFAULT_THINKING_LEVEL,
  ThinkingLevelOption,
  ThinkingMode,
  THINKING_BUDGET_OPTIONS,
  buildThinkingLevelOptions,
  CostAnalysis,
  VoiceChatState,
  VoiceHistoryEntry,
  VoiceResponse,
  VoiceTranscriptCorrectedEvent,
  VoiceTranscriptDecoratedEvent,
  SelectedTextContext,
  ChatContext,
} from '../../../models/chat.models';
import { ChatService } from '../../../services/chat.service';
import { ChatAttachmentService } from '../../../services/chat-attachment.service';
import { AttachmentThumbnailsComponent } from '../attachment-thumbnails/attachment-thumbnails.component';
import { ChatStateService, ContextFill } from '../../../services/chat-state.service';
import { VoiceChatService } from '../../../services/voice-chat.service';
import { Subscription } from 'rxjs';

/** A message as it leaves the composer: the typed text plus an immutable set of images. */
export interface ComposedMessage {
  text: string;
  attachments: ChatAttachment[];
}

/**
 * Chat input component with send button.
 *
 * Features:
 * - Auto-resizing textarea
 * - Enter to send (Shift+Enter for new line)
 * - Disabled state while loading
 */
@Component({
  selector: 'app-chat-input',
  imports: [
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    MatChipsModule,
    MatMenuModule,
    MatSelectModule,
    TextFieldModule,
    AttachmentThumbnailsComponent,
  ],
  templateUrl: './chat-input.component.html',
  styleUrls: ['./chat-input.component.scss'],
})
export class ChatInputComponent implements OnInit, OnChanges, OnDestroy {
  private chatService = inject(ChatService);
  private chatState = inject(ChatStateService);
  private attachmentService = inject(ChatAttachmentService);
  voiceService = inject(VoiceChatService);
  private voiceSubs: Subscription[] = [];

  @Input() disabled: boolean = false;
  @Input() isLoading: boolean = false;
  @Input() context: any; // Chat context (project, file, page, pinned pages)
  @Input() costAnalysis: CostAnalysis | undefined;
  @Input() selectedModel: string = DEFAULT_CHAT_MODEL_ID;
  @Input() selectedThinkingLevel: string = DEFAULT_THINKING_LEVEL;
  @Input() customBudget: number | null = null;
  @Input() sessionId: string | null = null;
  @Input() showStatusBar: boolean = false;
  /** Project the conversation belongs to, recorded on staged attachments for scoping and cleanup. */
  @Input() projectId: string | null = null;
  @Output() messageSent = new EventEmitter<ComposedMessage>();
  @Output() stopRequested = new EventEmitter<void>();
  @Output() modelChanged = new EventEmitter<string>();
  @Output() thinkingLevelChanged = new EventEmitter<string>();
  @Output() customBudgetChanged = new EventEmitter<number | null>();
  @Output() contextRemoved = new EventEmitter<'project' | 'file' | 'page'>();
  @Output() pinnedPageRemoved = new EventEmitter<any>();
  @Output() addContextClicked = new EventEmitter<void>();
  @Output() voiceTranscript = new EventEmitter<{
    role: 'user' | 'assistant';
    text: string;
    partial: boolean;
  }>();
  @Output() voiceStartRequested = new EventEmitter<void>();
  @Output() voiceToolCall = new EventEmitter<VoiceResponse>();
  @Output() voiceThinking = new EventEmitter<string>();
  @Output() voiceError = new EventEmitter<string>();
  @Output() voiceReconnected = new EventEmitter<void>();
  @Output() voiceTurnComplete = new EventEmitter<void>();
  @Output() voiceTranscriptDecorated = new EventEmitter<VoiceTranscriptDecoratedEvent>();
  @Output() voiceTranscriptCorrected = new EventEmitter<VoiceTranscriptCorrectedEvent>();

  message: string = '';
  isFocused: boolean = false;

  /** Images attached to the message currently being composed, in the order they were added. */
  attachments: ChatAttachment[] = [];

  /** True while a file is being dragged over the composer, used to show the drop target. */
  isDragOver: boolean = false;

  /**
   * Live-region text announcing attachment changes.
   *
   * Pasting an image has no visual moment of its own — no dialog opens, no button is pressed — so
   * without an announcement a screen-reader user would have no way of knowing an attachment
   * appeared. The same region reports removals and refusals.
   */
  attachmentAnnouncement: string = '';

  /** Limits mirrored from the backend, shown as a hint and used by the file picker. */
  readonly attachmentLimits = ATTACHMENT_LIMITS;

  @ViewChild('fileInput') fileInput?: ElementRef<HTMLInputElement>;

  private attachmentSubs: Subscription[] = [];
  private announcementTimer?: ReturnType<typeof setTimeout>;

  contextExpanded: boolean = false;
  voiceModeEnabled: boolean = false;
  voiceState: VoiceChatState = 'idle';
  audioLevel: number = 0;
  supportedModels: ChatModelOption[] = [];
  thinkingLevels: ThinkingLevelOption[] = THINKING_BUDGET_OPTIONS;
  customBudgetInput: string = '8192';
  selectedVoiceModelId: string | null = null;
  private lastVoiceSessionId: string | null = null;
  private lastVoiceHistory?: VoiceHistoryEntry[];
  sessionDuration: number = 0;
  inactivityCountdown: number | null = null;
  voiceTurnPhase: 'listening' | 'processing' = 'listening';

  readonly vuBars = [
    { threshold: 0.01 },
    { threshold: 0.03 },
    { threshold: 0.06 },
    { threshold: 0.1 },
    { threshold: 0.15 },
  ];

  get isCustomBudget(): boolean {
    return this.selectedThinkingLevel === 'CUSTOM';
  }

  get isVoiceActive(): boolean {
    return this.voiceModeEnabled || this.voiceState === 'connecting' || this.voiceState === 'reconnecting';
  }

  get isVoiceConnecting(): boolean {
    return this.voiceState === 'connecting' || this.voiceState === 'reconnecting';
  }

  get sessionDurationFormatted(): string {
    const m = Math.floor(this.sessionDuration / 60);
    const s = this.sessionDuration % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  }

  get inactivityFormatted(): string {
    if (this.inactivityCountdown === null) return '';
    const m = Math.floor(this.inactivityCountdown / 60);
    const s = this.inactivityCountdown % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  }

  get voiceStatusText(): string {
    if (this.voiceState === 'connecting') return 'Connecting...';
    if (this.voiceState === 'reconnecting') return 'Reconnecting...';
    if (this.isMuted) return 'Muted';
    return this.voiceTurnPhase === 'processing' ? 'Processing...' : 'Listening...';
  }

  get voiceTooltipText(): string {
    if (this.voiceState === 'active') return 'Stop voice chat';
    if (this.voiceState === 'reconnecting') return 'Stop reconnecting voice chat';
    return 'Start live voice chat';
  }

  get visibleModels(): ChatModelOption[] {
    return this.supportedModels.filter(m => (this.isVoiceActive ? m.voiceOnly : !m.voiceOnly));
  }

  /** Selected Live model, defaulting to the backend-designated rollout model. */
  get voiceModel(): ChatModelOption | undefined {
    const voiceModels = this.supportedModels.filter(model => model.voiceOnly);
    return (
      voiceModels.find(model => model.id === this.selectedVoiceModelId) ??
      voiceModels.find(model => model.defaultVoice) ??
      voiceModels[0]
    );
  }

  get displayedModelId(): string {
    return this.isVoiceActive ? (this.voiceModel?.id ?? '') : this.selectedModel;
  }

  get isVoiceAvailable(): boolean {
    return this.voiceModel !== undefined;
  }

  get currentThinkingMode(): ThinkingMode {
    const model = this.supportedModels.find(m => m.id === this.selectedModel);
    return model?.thinkingMode ?? 'BUDGET';
  }

  ngOnInit(): void {
    this.chatService.listModels().subscribe({
      next: models => {
        this.supportedModels = models;
        this.ensureSelectedVoiceModelIsSupported();
        this.ensureSelectedModelIsSupported();
        ClientLoggingService.log('INFO', '[ChatInput] Loaded models from API:', models.length);
        this.initContextFillForCurrentModel();
      },
      error: err => {
        ClientLoggingService.log('WARN', '[ChatInput] Failed to load models from API, using fallback:', err);
        this.supportedModels = [
          {
            id: 'gemini-3.8-flash',
            label: 'Gemini 3.8 Flash',
            thinkingMode: 'LEVEL',
            supportedThinkingLevels: ['LOW', 'MEDIUM', 'HIGH'],
          },
          {
            id: 'gemini-3.7-flash',
            label: 'Gemini 3.7 Flash',
            thinkingMode: 'LEVEL',
            supportedThinkingLevels: ['LOW', 'MEDIUM', 'HIGH'],
          },
          {
            id: 'gemini-3.6-flash',
            label: 'Gemini 3.6 Flash',
            thinkingMode: 'LEVEL',
            supportedThinkingLevels: ['LOW', 'MEDIUM', 'HIGH'],
          },
          {
            id: 'gemini-3.5-flash-lite',
            label: 'Gemini 3.5 Flash Lite',
            thinkingMode: 'LEVEL',
            supportedThinkingLevels: ['MINIMAL', 'LOW', 'MEDIUM', 'HIGH'],
          },
          {
            id: 'gemini-4-argon',
            label: 'Gemini 4 Argon',
            thinkingMode: 'LEVEL',
            supportedThinkingLevels: ['LOW', 'MEDIUM', 'HIGH'],
          },
        ];
        this.ensureSelectedModelIsSupported();
      },
    });

    this.attachmentSubs.push(
      this.attachmentService.drafts$.subscribe(drafts => (this.attachments = drafts)),
      this.attachmentService.announcements$.subscribe(message => this.announce(message))
    );
    this.attachments = this.attachmentService.currentDrafts;

    this.voiceSubs.push(
      this.voiceService.state$.subscribe(state => {
        const wasVoice =
          this.voiceState === 'active' || this.voiceState === 'connecting' || this.voiceState === 'reconnecting';
        this.voiceState = state;
        this.voiceModeEnabled = state === 'active' || state === 'reconnecting';

        const isVoice = state === 'active' || state === 'connecting' || state === 'reconnecting';
        if (isVoice && !wasVoice && !this.voiceModel) {
          ClientLoggingService.log('ERROR', '[ChatInput] Voice model is missing from the model catalog');
        }
      }),
      this.voiceService.audioLevel$.subscribe(level => (this.audioLevel = level)),
      this.voiceService.turnPhase$.subscribe(phase => (this.voiceTurnPhase = phase)),
      this.voiceService.transcript$.subscribe(t => this.voiceTranscript.emit(t)),
      this.voiceService.toolCall$.subscribe(tc => this.voiceToolCall.emit(tc)),
      this.voiceService.turnComplete$.subscribe(() => this.voiceTurnComplete.emit()),
      this.voiceService.thinking$.subscribe(text => this.voiceThinking.emit(text)),
      this.voiceService.error$.subscribe(err => {
        ClientLoggingService.log('ERROR', '[ChatInput] Voice error:', err);
        this.voiceError.emit(err);
      }),
      this.voiceService.reconnected$.subscribe(() => this.voiceReconnected.emit()),
      this.voiceService.tokenUsage$.subscribe(usage => this.chatState.setVoiceTokenUsage(usage)),
      this.voiceService.transcriptDecorated$.subscribe(e => this.voiceTranscriptDecorated.emit(e)),
      this.voiceService.transcriptCorrected$.subscribe(e => this.voiceTranscriptCorrected.emit(e)),
      this.voiceService.sessionDuration$.subscribe(d => (this.sessionDuration = d)),
      this.voiceService.inactivityCountdown$.subscribe(c => (this.inactivityCountdown = c))
    );
  }

  /** Replaces persisted model ids that are no longer available after a model migration. */
  private ensureSelectedModelIsSupported(): void {
    if (!this.supportedModels.some(model => model.id === this.selectedModel)) {
      this.modelChanged.emit(DEFAULT_CHAT_MODEL_ID);
    }
  }

  private ensureSelectedVoiceModelIsSupported(): void {
    const voiceModels = this.supportedModels.filter(model => model.voiceOnly);
    if (!voiceModels.some(model => model.id === this.selectedVoiceModelId)) {
      this.selectedVoiceModelId = voiceModels.find(model => model.defaultVoice)?.id ?? voiceModels[0]?.id ?? null;
    }
  }

  onModelSelectionChanged(modelId: string): void {
    if (!this.isVoiceActive) {
      this.modelChanged.emit(modelId);
      return;
    }
    if (!this.supportedModels.some(model => model.id === modelId && model.voiceOnly)) {
      return;
    }
    this.selectedVoiceModelId = modelId;
    if (this.voiceState === 'active' && this.lastVoiceSessionId) {
      // A model change creates a new Live connection and therefore a new assistant turn.
      // Close the current bubble before collecting history so the resumed model's
      // acknowledgment cannot be appended to the prior model's response.
      this.voiceReconnected.emit();
      const history = this.chatState.currentState.messages
        .filter(message => message.role === 'USER' || message.role === 'ASSISTANT')
        .filter(message => !!message.content?.trim())
        .slice(-20)
        .map(message => ({
          role: message.role as 'USER' | 'ASSISTANT',
          content: message.content.slice(0, 500),
        }));
      this.voiceService.stop();
      void this.voiceService.start(
        this.lastVoiceSessionId,
        { model: modelId },
        this.context,
        history.length > 0 ? history : this.lastVoiceHistory
      );
    }
  }

  ngOnDestroy(): void {
    this.voiceSubs.forEach(s => s.unsubscribe());
    this.attachmentSubs.forEach(s => s.unsubscribe());
    if (this.announcementTimer) {
      clearTimeout(this.announcementTimer);
    }
    // Deliberately does NOT clear draft attachments. Two reasons, both found after this method
    // originally did.
    //
    // It would not run. This composer lives unconditionally inside a mat-sidenav that keeps its
    // content mounted when closed, and the chat panel itself only unmounts on sign-out or a
    // navigation to the unauthorized route — so no ordinary gesture destroys it, and a release
    // hung here never fires.
    //
    // And it would be wrong if it did. ChatAttachmentService is provided in root, so the drafts
    // are application-scoped rather than owned by this component; discarding them on component
    // teardown would throw away images the user still has staged. Their object URLs are released
    // where the drafts themselves are — on removal and on send.
    //
    // When a draft counts as abandoned, and whether closing the panel should release one, is a
    // product question rather than a lifecycle detail: #1769 decides when a staged image counts as
    // abandoned, and will wire ChatAttachmentService.clearDrafts to whichever gesture is chosen.
    if (this.voiceState !== 'idle') {
      this.voiceService.stop();
    }
  }

  get tokensRead(): number {
    const tb = this.costAnalysis?.tokenBreakdown;
    return (tb?.nonCachedInput?.tokenCount ?? 0) + (tb?.cachedContent?.tokenCount ?? 0);
  }

  get tokensWritten(): number {
    return (
      (this.costAnalysis?.tokenBreakdown?.output?.tokenCount ?? 0) +
      (this.costAnalysis?.tokenBreakdown?.thinking?.tokenCount ?? 0) +
      (this.costAnalysis?.tokenBreakdown?.toolUse?.tokenCount ?? 0)
    );
  }

  get totalCost(): number {
    return this.costAnalysis?.estimatedTotalCostUsd ?? 0;
  }

  get contextFill(): ContextFill | null {
    return this.chatState.currentState.contextFill;
  }

  get contextFillColor(): string {
    const fill = this.contextFill;
    if (!fill || fill.fillPercent === 0) return 'context-fill-neutral';
    if (fill.fillPercent >= 80) return 'context-fill-red';
    if (fill.fillPercent >= 60) return 'context-fill-yellow';
    return 'context-fill-green';
  }

  get contextFillTooltip(): string {
    const fill = this.contextFill;
    if (!fill || fill.promptTokens === 0) {
      return (
        'Context window: ' +
        (fill?.contextWindowSize?.toLocaleString() ?? '?') +
        ' tokens available. Shows current input size vs model capacity (per-call limit, not cumulative).'
      );
    }
    return (
      'Context: ' +
      fill.promptTokens.toLocaleString() +
      ' / ' +
      fill.contextWindowSize.toLocaleString() +
      ' tokens (current input size vs model capacity — per-call limit, not cumulative)'
    );
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['context']) {
      ClientLoggingService.log('INFO', '[ChatInput] Context changed:', this.context);
      ClientLoggingService.log('INFO', '[ChatInput] hasContext():', this.hasContext());
      const prevQuotes = changes['context'].previousValue?.selectedTexts?.length ?? 0;
      const currQuotes = changes['context'].currentValue?.selectedTexts?.length ?? 0;
      if (currQuotes > prevQuotes) {
        this.contextExpanded = true;
      }
    }
    if (changes['selectedModel']) {
      this.updateThinkingOptions();
      const model = this.supportedModels.find(m => m.id === this.selectedModel);
      if (model?.contextWindowSize) {
        this.chatState.recalculateContextFillForModel(model.contextWindowSize);
      } else {
        this.chatState.clearContextFill();
      }
    }
    if (changes['customBudget'] && this.customBudget != null) {
      this.customBudgetInput = this.customBudget.toString();
    }
  }

  private initContextFillForCurrentModel(): void {
    if (this.chatState.currentState.contextFill) return;
    const model = this.supportedModels.find(m => m.id === this.selectedModel);
    if (model?.contextWindowSize) {
      this.chatState.recalculateContextFillForModel(model.contextWindowSize);
    }
  }

  private updateThinkingOptions(): void {
    const mode = this.currentThinkingMode;
    if (mode === 'LEVEL') {
      const model = this.supportedModels.find(m => m.id === this.selectedModel);
      this.thinkingLevels = buildThinkingLevelOptions(model?.supportedThinkingLevels);
    } else {
      this.thinkingLevels = THINKING_BUDGET_OPTIONS;
    }

    const validIds = this.thinkingLevels.map(l => l.id);
    if (!validIds.includes(this.selectedThinkingLevel)) {
      const fallback = validIds.includes(DEFAULT_THINKING_LEVEL)
        ? DEFAULT_THINKING_LEVEL
        : validIds[validIds.length - 1];
      this.thinkingLevelChanged.emit(fallback);
    }
  }

  onCustomBudgetChange(value: string): void {
    const parsed = parseInt(value, 10);
    if (!isNaN(parsed) && parsed >= 0) {
      this.customBudgetChanged.emit(parsed);
    }
  }

  /**
   * Check if has any context
   */
  hasContext(): boolean {
    return !!(
      this.context?.projectId ||
      this.context?.fileId ||
      this.context?.pageNumber ||
      (this.context?.pinnedPages && this.context.pinnedPages.length > 0) ||
      (this.context?.selectedTexts && this.context.selectedTexts.length > 0)
    );
  }

  onFocus() {
    this.isFocused = true;
  }

  onBlur() {
    this.isFocused = false;
  }

  /**
   * Remove context item
   */
  removeContextItem(type: 'project' | 'file' | 'page') {
    this.contextRemoved.emit(type);
  }

  /**
   * Remove pinned page
   */
  removePinnedPage(page: any) {
    this.pinnedPageRemoved.emit(page);
  }

  /**
   * Remove selected text quote context (Issue #1745)
   */
  removeSelectedText(text: SelectedTextContext | string) {
    this.chatState.removeSelectedText(text);
  }

  /**
   * Open context dialog
   */
  openContextDialog() {
    this.addContextClicked.emit();
  }

  /**
   * Toggle context chips visibility with animation
   */
  toggleContext() {
    this.contextExpanded = !this.contextExpanded;
  }

  /**
   * Send message
   */
  send() {
    if (this.disabled || !this.canSend) {
      return;
    }
    // Taking the drafts empties the composer's list in the same step that builds the message, so
    // the turn that goes out is a snapshot. Attaching or removing an image afterwards affects the
    // next message and can never reach back into one that is queued, retrying, or already sent.
    const attachments = this.attachmentService.takeReadyDrafts();
    this.messageSent.emit({ text: this.message.trim(), attachments });
    this.message = '';
  }

  /**
   * Whether the send button is offered.
   *
   * A message needs either text or at least one image that finished uploading. An image-only
   * message is allowed — someone pasting a screenshot of an error dialog often has nothing to add
   * in words — and the backend supplies the model with a default instruction for that case rather
   * than the composer inventing message text on the user's behalf.
   */
  get canSend(): boolean {
    if (this.isVoiceActive) return false;
    if (this.hasUnsettledAttachments) return false;
    return !!this.message.trim() || this.attachments.some(a => a.state === 'ready');
  }

  /** True while any attachment is still uploading or being validated. */
  get hasUnsettledAttachments(): boolean {
    return this.attachments.some(a => a.state === 'uploading' || a.state === 'validating');
  }

  /** Whether the selected model can read images at all. */
  get modelSupportsImages(): boolean {
    const model = this.supportedModels.find(m => m.id === this.selectedModel);
    // Absent means the backend has not been redeployed with the capability flag yet. Every text
    // model in the selector accepts images, so treating an unknown value as capable keeps the
    // control available rather than hiding it on a stale model list.
    return model?.imageInputSupported !== false;
  }

  /**
   * Whether the formats-and-limits fine print under the composer is shown (Issue #2908).
   *
   * Most messages carry no image, so the line appears only once the message holds at least one
   * attachment. During a drag the drop overlay states the same limits itself, so the line stays
   * hidden then rather than adding a second layer of text. When hidden it remains in the page as
   * screen-reader-only text for the attach item's description.
   */
  get showAttachmentHint(): boolean {
    return this.attachments.length > 0;
  }

  /** Whether the attach control is offered right now, and why not when it is not. */
  get canAttach(): boolean {
    return this.attachmentsPossible && this.attachmentService.canAddMore;
  }

  /**
   * Whether attaching is possible in principle, deliberately ignoring the per-message cap.
   *
   * Drop and paste use this rather than {@link canAttach} so that a file refused for exceeding the
   * cap still reaches the service, which refuses it *visibly* — the same failed placeholder, with
   * the same visible sentence, that an unsupported type or an oversized file already produces.
   * Turning drop and paste away here instead published the reason only to the hidden live region,
   * so a fifth dropped image vanished without explanation.
   *
   * The other two reasons stay a refusal at this level, because each already has a visible account
   * of itself elsewhere on screen: a voice conversation is plainly in progress, and a model that
   * cannot read images is named in the selector beside the disabled control.
   */
  get attachmentsPossible(): boolean {
    return !this.disabled && !this.isVoiceActive && this.modelSupportsImages;
  }

  /** Tooltip and accessible description for the attach control. */
  get attachTooltip(): string {
    if (this.isVoiceActive) {
      return 'Images cannot be attached during a voice conversation.';
    }
    if (!this.modelSupportsImages) {
      return 'The selected model cannot read images. Choose a text model to attach one.';
    }
    if (!this.attachmentService.canAddMore) {
      return `A message can carry at most ${ATTACHMENT_LIMITS.maxAttachmentsPerMessage} images.`;
    }
    return 'Attach a PNG or JPEG image (you can also drag one in or paste from the clipboard)';
  }

  /** Opens the system file picker. Drag-and-drop is never the only way in. */
  openFilePicker(): void {
    this.fileInput?.nativeElement.click();
  }

  /** Handles files chosen through the picker. */
  async onFilesChosen(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const files = Array.from(input.files ?? []);
    // Reset first so choosing the same file twice in a row still fires a change event.
    input.value = '';
    if (files.length > 0) {
      await this.attachmentService.addFiles(files, this.projectId ?? undefined);
    }
  }

  onDragOver(event: DragEvent): void {
    // The cap is not consulted here, and that is load-bearing rather than cosmetic. A drop event
    // only fires at all when dragover cancels the default, so gating this on the cap meant a fifth
    // image dropped onto a full composer never reached onDrop, and the refusal that onDrop would
    // have explained was never even reached to be explained.
    if (!this.attachmentsPossible || !ChatAttachmentService.dragCarriesFiles(event)) return;
    event.preventDefault();
    this.isDragOver = true;
  }

  onDragLeave(event: DragEvent): void {
    // Moving between child elements fires dragleave on the parent; only clear the highlight when
    // the pointer has actually left the composer.
    if (event.currentTarget instanceof Node && event.relatedTarget instanceof Node) {
      if (event.currentTarget.contains(event.relatedTarget)) return;
    }
    this.isDragOver = false;
  }

  async onDrop(event: DragEvent): Promise<void> {
    if (!ChatAttachmentService.dragCarriesFiles(event)) return;
    event.preventDefault();
    this.isDragOver = false;
    if (!this.attachmentsPossible) {
      this.announce(this.attachTooltip);
      return;
    }
    const files = Array.from(event.dataTransfer?.files ?? []);
    if (files.length > 0) {
      await this.attachmentService.addFiles(files, this.projectId ?? undefined);
    }
  }

  /**
   * Handles a paste into the message box.
   *
   * The clipboard often holds an image and text at the same time. Only the image half is taken
   * here, and the event is deliberately not cancelled when text is also present, so the browser
   * still performs its own text paste and the user keeps both.
   */
  async onPaste(event: ClipboardEvent): Promise<void> {
    const images = ChatAttachmentService.imagesFromClipboard(event);
    if (images.length === 0) return;

    const hasText = !!event.clipboardData?.getData('text/plain');
    if (!hasText) {
      // Nothing else to paste; stop the browser inserting a stray file name.
      event.preventDefault();
    }
    if (!this.attachmentsPossible) {
      this.announce(this.attachTooltip);
      return;
    }
    await this.attachmentService.addFiles(images, this.projectId ?? undefined, true);
  }

  /** Removes one attached image from the message being composed. */
  async removeAttachment(attachmentId: string): Promise<void> {
    await this.attachmentService.remove(attachmentId);
  }

  /**
   * Publishes a message to the live region.
   *
   * The text is cleared shortly afterwards so that the same message announced twice in a row —
   * pasting the same screenshot twice, for instance — is spoken both times rather than being
   * treated as an unchanged region and skipped.
   */
  private announce(message: string): void {
    this.attachmentAnnouncement = message;
    if (this.announcementTimer) {
      clearTimeout(this.announcementTimer);
    }
    this.announcementTimer = setTimeout(() => (this.attachmentAnnouncement = ''), 4000);
  }

  /**
   * Handle keyboard events
   * Enter: send message
   * Shift+Enter: new line
   */
  onKeydown(event: KeyboardEvent) {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.send();
    }
    // Shift+Enter allows natural newline
  }

  get isMuted(): boolean {
    return this.voiceService.isMuted;
  }

  /**
   * Toggle live voice chat (Issue #802).
   * If no session exists yet, emits voiceStartRequested so the parent
   * can create one first. Otherwise starts/stops directly.
   */
  toggleVoiceMode(): void {
    if (this.voiceState === 'active' || this.voiceState === 'connecting' || this.voiceState === 'reconnecting') {
      this.voiceService.stop();
    } else {
      this.voiceStartRequested.emit();
    }
  }

  /** Called by parent after ensuring a session exists. */
  startVoiceWithSession(
    sessionId: string,
    conversationHistory?: VoiceHistoryEntry[],
    context: ChatContext = this.context
  ): void {
    this.lastVoiceSessionId = sessionId;
    this.lastVoiceHistory = conversationHistory;
    this.voiceService.start(sessionId, { model: this.voiceModel?.id }, context, conversationHistory);
  }

  /** Toggle microphone mute during an active voice session. */
  toggleMute(): void {
    this.voiceService.toggleMute();
  }
}
